import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { RoomMatchMockup } from '../components/visuals/RoomMatchMockup.js';
import { IconClinic } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-allied-health')({
  component: AlliedHealthPage,
});

function AlliedHealthPage() {
  usePageTitle('Bookings for clinics & allied health');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For clinics & allied health',
  icon: IconClinic,
  headline: 'Match each practitioner with the right room.',
  headlineEmphasis: 'right room',
  subhead:
    'Bookaburra knows which services need which rooms, so reception does not have to hold the whole clinic map in their head.',
  Visual: RoomMatchMockup,
  business: {
    name: 'Harbourside Medical',
    location: 'Sydney · 2 practitioners · 3 rooms',
    summary:
      'Imagine a small Australian GP-plus-physio practice. Two consult rooms suit GP work; a separate treatment room is set up for physio. Two practitioners share the space: a GP and a physiotherapist. The room mix is small, but the cost of a wrong allocation is a patient turned away or a practitioner standing in the hallway.',
  },
  demoBookingSlug: 'harbourside-medical',
  dayTimeline: [
    {
      time: '8:30',
      event:
        'Day’s first booking: Mrs Watson, 15-minute standard with Dr Sam in Consult Room 1.',
      bookaburraResponse:
        'That’s the room rule doing its job: when the booking was made, only consult rooms were offered for a GP service.',
    },
    {
      time: '9:40',
      event:
        'A new patient walks in asking for a 30-minute long consult today. The 10:00–10:15 gap looks free.',
      bookaburraResponse:
        'Only offers full 30-minute slots in a consult room. Books her into 11:00, not the 15-minute gap.',
    },
    {
      time: '10:50',
      event:
        'Reception fill-in opens the calendar and tries to drag Mia’s 11:30 physio into Consult Room 2.',
      bookaburraResponse:
        'The system blocks the move before it commits — physio services require the treatment-room feature, and the move would break that rule.',
    },
    {
      time: '14:20',
      event:
        'Dr Sam runs over on a complex consult; the 14:30 patient is in the waiting room.',
      bookaburraResponse:
        'Nothing automatic, but the patient has the magic-link reschedule in his SMS reminder if he needs to bail.',
    },
    {
      time: '15:45',
      event:
        'A long-standing patient books in for next Tuesday from his appointment reminder email.',
      bookaburraResponse:
        'Self-serve magic link skips reception entirely. The booking lands on Mia’s calendar feed without double entry.',
    },
  ],
  pains: [
    {
      quote:
        'The physio took the next free room, which was a consult room. The GP’s 9:15 walked in to find nowhere to sit.',
      explanation:
        'Everyone knows which room is right. The calendar should enforce it before the mistake happens.',
    },
    {
      quote:
        'A long consult got booked into the same slot as a standard, and we found out when both patients arrived.',
      explanation:
        'A start time is not enough. The booking has to fit the full appointment.',
    },
    {
      quote:
        "We send appointment reminders in English. Half our clients don’t read English first.",
      explanation:
        'Clear reminders reduce phone calls and missed preparation.',
    },
  ],
  solutions: [
    {
      title: 'Stop room mix-ups',
      body: (
        <>
          GP consults need consult rooms. Physio sessions need the treatment
          room. Bookaburra only offers slots where the right room is free.
        </>
      ),
    },
    {
      title: 'Stop squeezing long consults',
      body: (
        <>
          A 30-minute consult cannot be squeezed into a 15-minute gap. Slots use
          the service duration and your buffers.
        </>
      ),
    },
    {
      title: 'Reduce reminder phone calls',
      body: (
        <>
          Reminders and prep instructions use the client&rsquo;s preferred language
          and approved wording.
        </>
      ),
    },
    {
      title: 'Stop double-entering calendars',
      body: (
        <>
          Each practitioner can subscribe to a private calendar feed. New,
          moved, and cancelled bookings show up without double entry.
        </>
      ),
    },
  ],
  industrySlug: 'clinics',
};
